import { tracked } from "@glimmer/tracking";
import { Step, type StepInit } from "./step.ts";
import { trackedArray } from "@ember/reactive/collections";
import { Sound } from "./sound.ts";

export interface ChannelInit {
  sound?: Sound;
  volume?: number;
  isMuted?: boolean;
  steps?: Step[];
}

export class Channel {
  @tracked sound: Sound;
  @tracked volume: number;
  @tracked isMuted: boolean;
  @tracked steps: Step[];

  constructor(init: ChannelInit = {}) {
    this.sound = init.sound ?? Sound.DEFAULT;
    this.volume = init.volume ?? 1;
    this.isMuted = init.isMuted ?? false;
    this.steps = trackedArray(
      init.steps ? init.steps : Array.from({ length: 16 }, () => new Step()),
    );
  }

  toString() {
    // not a unique id
    const activeSteps: string[] = [];
    (this.steps || []).forEach((step, i) => {
      if (step.enabled) {
        if (step.velocity === 0.5) {
          activeSteps.push(`${i}*`);
        } else if (step.velocity !== 1.0) {
          activeSteps.push(`${i} (${step.velocity})`);
        } else {
          activeSteps.push(`${i}`);
        }
      }
    });
    return `Channel:${this.sound.id}:${this.volume}:${this.isMuted ? "x" : "+"}:${this.steps.length}:${activeSteps.join("/")}`;
  }

  addStep(init: StepInit = {}): Step {
    const step = new Step(init);
    this.steps.push(step);
    return step;
  }

  removeStep(): Step | undefined {
    return this.steps.pop();
  }

  serialize(): { sound: string; volume: number; steps: { velocity: number }[] } {
    return {
      sound: this.sound.id,
      volume: this.volume,
      steps: this.steps.map((step) => step.serialize()),
    };
  }

  static deserialize(data: {
    sound?: string;
    volume?: number;
    steps?: { velocity?: number }[];
  }): Channel {
    const steps = (data.steps ?? []).map((stepData) => Step.deserialize(stepData));
    const sound = Sound.findById(data.sound) ?? Sound.DEFAULT;
    return new Channel({
      sound,
      volume: data.volume ?? 1,
      steps,
    });
  }
}
